import type {
    HalImageChannelLayout,
    HalImageDataType,
    HalImageDescriptor,
    HalImageSourceData,
    NativeImageData,
    PartialHalImageDescriptor,
    TypedArrayImage,
} from "#hal/hal-image/types"
import {assertNever, getMipLevelBiasScale as getMipLevelBiasScaleUtils, getMipLevelSize as getMipLevelSizeUtils, getNumCeilMipLevels} from "@cm/utils"
import type {Size2Like} from "@cm/math"
import type {HalImage, HalImagePhysical, HalImageView, HalImageVirtual} from "#hal/hal-image/index"

export function isHalImageDescriptor(obj?: unknown): obj is PartialHalImageDescriptor {
    return (
        obj != undefined &&
        typeof obj === "object" &&
        "width" in obj &&
        obj.width != undefined &&
        "height" in obj &&
        obj.height != undefined &&
        "channelLayout" in obj &&
        obj.channelLayout != undefined &&
        "dataType" in obj &&
        obj.dataType != undefined
    )
}

export function isHalImage(data: unknown): data is HalImage {
    return data != null && typeof data === "object" && "isHalImage" in data && data.isHalImage === true
}

export function isHalImagePhysical(data: unknown): data is HalImagePhysical {
    return data != null && typeof data === "object" && "isHalImagePhysical" in data && data.isHalImagePhysical === true
}

export function isHalImageVirtual(data: unknown): data is HalImageVirtual {
    return data != null && typeof data === "object" && "isHalImageVirtual" in data && data.isHalImageVirtual === true
}

export function isHalImageView(data: unknown): data is HalImageView {
    return data != null && typeof data === "object" && "isHalImageView" in data && data.isHalImageView === true
}

export const getNumMipLevels = getNumCeilMipLevels
export const getMipLevelBiasScale = getMipLevelBiasScaleUtils
export const getMipLevelSize = getMipLevelSizeUtils

export const completeHalImageDescriptor = (descriptor: PartialHalImageDescriptor): HalImageDescriptor => {
    return {
        ...descriptor,
        numLayers: descriptor.numLayers ?? 1,
        numMipLevels: descriptor.numMipLevels === 0 ? getNumMipLevels(descriptor) : (descriptor.numMipLevels ?? 1),
    }
}

export function getNumChannels(layout: HalImageChannelLayout): number {
    switch (layout) {
        case "RGBA":
            return 4
        case "RGB":
            return 3
        case "R":
            return 1
    }
    assertNever(layout)
}

export function getBytesPerChannel(dataType: HalImageDataType): number {
    switch (dataType) {
        case "uint8":
        case "uint8norm":
        case "uint8norm_srgb":
            return 1
        case "float16":
            return 2
        case "float32":
            return 4
    }
    assertNever(dataType)
}

export function isTypedArrayImage(data: unknown): data is TypedArrayImage {
    return data instanceof Object && "width" in data && "height" in data && "data" in data
}

// export function getDataTypeFromTypedArray(data: TypedArrayImage["data"]): HalImageDataType {
//     if (data instanceof Uint8ClampedArray) {
//         return "uint8norm"
//     } else if (data instanceof Uint8Array) {
//         return "uint8norm"
//     } else if (data instanceof Uint16Array) {
//         return "float16"
//     } else if (data instanceof Float32Array) {
//         return "float32"
//     } else {
//         assertNever(data)
//     }
// }

export function getDataTypeFromImageSourceData(data: HalImageSourceData): HalImageDataType {
    return isTypedArrayImage(data) ? data.dataType : isNativeImageData(data) ? (data.isSrgb ? "uint8norm_srgb" : "uint8norm") : data.descriptor.dataType
}

export function getChannelLayoutFromImageSourceData(data: HalImageSourceData): HalImageChannelLayout {
    return isTypedArrayImage(data) ? data.channelLayout : isNativeImageData(data) ? "RGBA" : data.descriptor.channelLayout
}

export function createTypedArrayImage(args: {
    width: number
    height: number
    channelLayout: HalImageChannelLayout
    dataType: HalImageDataType
    data: Uint8ClampedArray | Uint8Array | Uint16Array | Float32Array
}): TypedArrayImage {
    const numChannels = getNumChannels(args.channelLayout)
    if (args.data.length !== args.width * args.height * numChannels) {
        throw new Error("Invalid data length")
    }
    switch (args.dataType) {
        case "uint8":
        case "uint8norm":
        case "uint8norm_srgb":
            if (!(args.data instanceof Uint8Array || args.data instanceof Uint8ClampedArray)) {
                throw new Error("Invalid array type")
            }
            break
        case "float16":
            if (!(args.data instanceof Uint16Array)) {
                throw new Error("Invalid array type")
            }
            break
        case "float32":
            if (!(args.data instanceof Float32Array)) {
                throw new Error("Invalid array type")
            }
            break
        default:
            assertNever(args.dataType)
    }
    return {
        width: args.width,
        height: args.height,
        channelLayout: args.channelLayout,
        dataType: args.dataType,
        data: args.data,
    }
}

export function isNativeImageData(data: unknown): data is NativeImageData {
    return (
        data instanceof Object &&
        "isSrgb" in data &&
        "data" in data &&
        (data.data instanceof HTMLImageElement || data.data instanceof HTMLCanvasElement || data.data instanceof ImageBitmap || data.data instanceof ImageData)
    )
}

export function createNativeImageData(isSrgb: boolean, data: HTMLImageElement | HTMLCanvasElement | ImageData): NativeImageData {
    return {isSrgb, data}
}

export function getSourceDataSize(sourceData: HalImageSourceData): Size2Like {
    if (isTypedArrayImage(sourceData)) {
        return {width: sourceData.width, height: sourceData.height}
    } else if (isNativeImageData(sourceData)) {
        return {width: sourceData.data.width, height: sourceData.data.height}
    } else {
        return {width: sourceData.descriptor.width, height: sourceData.descriptor.height}
    }
}
